import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Image as ImageIcon, ShieldCheck, Zap, Download } from "lucide-react";
import FileDropzone from "@/components/FileDropzone";
import EobEraSplitFlow from "@/components/EobEraSplitFlow";
import ActionButton from "@/components/ActionButton";
import { takePendingEob } from "@/lib/uploadBuffer";

// Full-screen "Upload EOB" experience. Reached from the Payments & ERA "Upload EOB" button,
// which opens the OS file picker and hands the chosen file over via the upload buffer —
// so this page usually lands straight on the loading pipeline. Direct navigation falls back
// to an in-page dropzone. Reuses the Experience Mode EOB → ERA pipeline (shared PipelineTile /
// StatusFooter indicators), sized for a full page. No autoLink → real, honest validation.
export default function UploadEob() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [toolbar, setToolbar] = useState(null);

  // Pick up the file handed over by the Payments "Upload EOB" button (file picker → navigate).
  useEffect(() => { const f = takePendingEob(); if (f) setFile(f); }, []);

  const reset = () => { setFile(null); setToolbar(null); };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="upload-eob-page">
      {/* Header */}
      <div className="w-full flex-shrink-0 mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[24px] font-bold tracking-[-0.7px] text-[#000] leading-none">Upload EOB</h1>
          <p className="text-[12px] text-[#6e6e73] mt-1.5">
            Drop a payer remittance and revQ reads it, decodes every CARC code, and drafts a balanced ERA (835).
          </p>
        </div>
        {toolbar ? (
          <div className="flex items-center gap-2 flex-shrink-0" data-testid="upload-eob-toolbar">
            <ActionButton onClick={toolbar.onDownload} variant="solid" icon={Download} data-testid="upload-eob-download-btn">
              Download ERA (835)
            </ActionButton>
            {toolbar.onSource ? (
              <ActionButton onClick={toolbar.onSource} icon={FileText} data-testid="upload-eob-source-btn">
                Source PDF
              </ActionButton>
            ) : null}
            {toolbar.onToggleRaw ? (
              <ActionButton onClick={toolbar.onToggleRaw} data-testid="upload-eob-raw-btn">
                {toolbar.showRaw ? "Hide 835" : "Raw 835"}
              </ActionButton>
            ) : null}
          </div>
        ) : null}
      </div>

      {!file ? (
        <div className="max-w-[720px] mx-auto w-full">
          <FileDropzone
            onFile={setFile}
            accept=".pdf,image/png,image/jpeg"
            hint="A PDF or photo of the payer's Explanation of Benefits. revQ extracts the payer, check, claims and line-level adjudication automatically."
            formats={[{ icon: FileText, label: "PDF" }, { icon: ImageIcon, label: "PNG / JPG" }]}
            testId="upload-eob-dropzone"
            inputTestId="upload-eob-input"
            browseTestId="upload-eob-browse"
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <Feature icon={Zap} title="Seconds, not minutes" sub="OCR → claim match → CARC decode → 835." />
            <Feature icon={ShieldCheck} title="Balanced & validated" sub="Each remit is balanced before an ERA is drafted." />
            <Feature icon={FileText} title="Standard X12 835" sub="View, download or post the remittance." />
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 w-full">
          <EobEraSplitFlow
            file={file}
            onChooseFlow={reset}
            onToolbar={setToolbar}
            onFinish={() => navigate("/payments")}
          />
        </div>
      )}
    </div>
  );
}

function Feature({ icon: Icon, title, sub }) {
  return (
    <div className="rounded-[14px] border border-black/[.07] bg-white/60 px-4 py-3.5">
      <div className="w-7 h-7 rounded-[9px] bg-[#111110] text-white flex items-center justify-center mb-2.5">
        <Icon size={14} strokeWidth={1.9} />
      </div>
      <div className="text-[12px] font-bold text-[#000]">{title}</div>
      <div className="text-[10.5px] text-[#6e6e73] mt-0.5 leading-snug">{sub}</div>
    </div>
  );
}
